import { Component, OnInit, ViewChild } from '@angular/core';
import { SelectItem } from 'primeng/api';
import { IOrganisationFilter } from '../../interfaces/organisation-filter';
import { OrganisationsService } from '../../services/organisations.service';
import { AutoJobMatchService } from '../../services/auto-job-match.service';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { IAutoMatchSettings } from '../../interfaces/auto-job-matching/auto-match-settings';
import { AutoMatchJobInfo } from '../../interfaces/auto-job-matching/auto-match-job-info';
import { AutoMatchOrderInfo } from '../../interfaces/auto-job-matching/auto-match-order-info';

@Component({
  selector: 'auto-job-match-settings',
  styleUrls: ['./auto-job-match-settings.component.scss'],
  templateUrl: 'auto-job-match-settings.component.html',
})
export class AutoJobMatchSettingsComponent implements OnInit {
  public excludedCustomers: SelectItem[];
  public includedCustomers: SelectItem[];
  public includeItem: SelectItem[];
  public excludeItem: SelectItem[];
  public newProfileName: string = '';
  public excludedCount = null;
  public customerChoices: SelectItem[];
  public exclusionChoices: SelectItem[];
  public filterCustomerChoices: SelectItem[] = [];
  public autoMatchChoices: SelectItem[] = [];
  public selectedSettingsId: number;
  public showEditSettings: boolean = false;
  public showNewSettingsDialog: boolean = false;
  public showDeleteSettingsDialog: boolean = false;
  public formGroup: UntypedFormGroup;
  public fuelTypes: SelectItem[] = [
    { value: 'petrol', label: 'Petrol' },
    { value: 'diesel', label: 'Diesel' },
    { value: 'electric', label: 'Electric' },
  ];

  public loadingMatches: boolean;
  public loadedMatchesId?: number = null;
  public profileMatches: AutoMatchJobInfo[];
  public matchedJobsData; // summary info from profileMatches - user clicks to see full details
  public selectedMatchedJob;
  public viewMatch: AutoMatchJobInfo;
  public viewMatchOrder: AutoMatchOrderInfo;

  public treeTableCols: any;

  constructor(
    private organisationsService: OrganisationsService,
    private formBuilder: UntypedFormBuilder,
    private jobCombinationService: AutoJobMatchService,
  ) {}

  async ngOnInit() {
    this.treeTableCols = [
      { field: 'jobNumber', header: 'JobNumber' },
      { field: 'scheduledDate', header: 'Scheduled Date' },
      { field: 'orders', header: 'Orders' },
    ];

    this.getAutoMatchSettingsChoices();
    this.getCustomerChoices();
  }

  get f() {
    return this.formGroup.controls;
  }

  moveToExcluded(item) {
    let index = this.includedCustomers.indexOf(item);
    this.excludedCustomers = [...this.excludedCustomers, item];
    this.includedCustomers = this.includedCustomers
      .slice(0, index)
      .concat(this.includedCustomers.slice(index + 1));
    this.jobCombinationService.addCustomerExclude(this.selectedSettingsId, item.value);
  }

  moveToIncluded(item) {
    let index = this.excludedCustomers.indexOf(item);
    this.includedCustomers = [...this.includedCustomers, item];
    this.excludedCustomers = this.excludedCustomers
      .slice(0, index)
      .concat(this.excludedCustomers.slice(index + 1));

    this.jobCombinationService
      .removeCustomerExclude(this.selectedSettingsId, item.value)
      .then((x) => {
        this.buildExcludedCustomers(this.selectedSettingsId);
      });

    this.excludedCount = this.excludedCustomers.length;
  }

  newSetting() {
    this.newProfileName = '';
    this.showNewSettingsDialog = true;
  }

  buildForm() {
    this.formGroup = this.formBuilder.group({
      profileName: ['', Validators.required],
      enabled: [true, ''],
      minWeightKg: [0, [Validators.required, Validators.min(0), Validators.max(20000)]],
      maxWeightKg: [3500, Validators.required],
      fuelTypeInclusionsArray: ['', Validators.required],
      excludeMultiTaskJobs: [true, Validators.required],
      matchingWaitTimeInMinutes: ['', Validators.required],
      totalJobDurationMinutes: ['', Validators.required],
      timeWindowWidthHours: ['', Validators.required],
      minTimeToLatestPickUpHours: ['', Validators.required],
      atoDMaxDistanceMiles: ['', Validators.required],
      rRatio: ['', Validators.required],
      atoDMaxTravelTimeMinutes: ['', Validators.required],
      minLeewayMinutes: ['', Validators.required],
      minTransitLeewayMinutes: ['', Validators.required],
      rRatioLessThanDay: ['', Validators.required],
      lessThanDaysWorkHours: ['', Validators.required],
      abbaMatchingAdditionalRadiusPer100Miles: ['', Validators.required],
      abbaMatchingMaximumRadius: ['', Validators.required],
      matchingRadiusPickup: ['', Validators.required],
      matchingRadiusDropoff: ['', Validators.required],
    });
  }

  getCustomerChoices() {
    var organisationFilter: IOrganisationFilter = {
      filters: {
        organisationTypeId: { value: 2 },
      },
    };

    this.organisationsService.getSelectList(organisationFilter).then((x) => {
      this.customerChoices = x.slice(); // Clone the array
    });
  }

  findInvalidControls() {
    const invalid = [];
    const controls = this.formGroup.controls;
    for (const name in controls) {
      if (controls[name].invalid) {
        console.log(name);
      }
    }
  }

  async saveSetting() {
    this.findInvalidControls();

    if (!this.formGroup.valid) {
      console.log('FORM INVALID');
      return;
    }

    var saveObject: IAutoMatchSettings;

    saveObject = this.formObject();

    var fuelTypes = [];

    this.f.fuelTypeInclusionsArray.value.forEach((x) => {
      fuelTypes.push(x);
    });

    var fuelTypesString = fuelTypes.toString();

    saveObject.id = this.selectedSettingsId;
    saveObject.fuelTypeInclusions = fuelTypesString;

    this.jobCombinationService.saveSetting({ autoMatchSettings: saveObject }).then((response) => {
      this.formGroup.patchValue({
        id: response.id,
      });

      this.getAutoMatchSettingsChoices();
    });
  }

  deleteSetting() {
    this.showDeleteSettingsDialog = true;
  }

  confirmDeleteSetting() {
    this.jobCombinationService.deleteSetting(this.selectedSettingsId).then((response) => {
      this.showDeleteSettingsDialog = false;
      this.showEditSettings = false;
      this.getAutoMatchSettingsChoices();
    });
  }

  formObject() {
    var f = this.formGroup.value;

    var x = { x, ...this.formGroup.value };

    return x;
  }

  getAutoMatchSettingsChoices() {
    this.jobCombinationService.getAutoMatchSettingsList().then((response) => {
      this.autoMatchChoices = [];

      this.autoMatchChoices.push({ value: 0, label: 'Select a setting profile' });

      response.forEach((x) => {
        var item = <SelectItem>{ value: x.id, label: x.profileName };

        this.autoMatchChoices.push(item);
      });
    });
  }

  createSettings() {
    this.jobCombinationService
      .createSetting({
        profileName: this.newProfileName,
        enabled: true,
      })
      .then((response: IAutoMatchSettings) => {
        this.showNewSettingsDialog = false;
        this.editSettings(response.id);
      });
  }

  editSettings(settingsId: number) {
    this.buildForm();
    this.showEditSettings = true;
    this.selectedSettingsId = settingsId;
    this.buildExcludedCustomers(settingsId);

    this.jobCombinationService
      .getAutoMatchSettings(settingsId)
      .then((response: IAutoMatchSettings) => {
        console.log('SETTINGS: ', response);

        this.formGroup.patchValue(response);

        if (response.fuelTypeInclusions != null && response.fuelTypeInclusions.length > 0) {
          this.formGroup.patchValue({
            fuelTypeInclusionsArray: response.fuelTypeInclusions.split(','),
          });
        }
      });
  }

  buildExcludedCustomers(settingsId: number) {
    var excluded = [];
    var included = this.customerChoices;

    this.excludedCount = 0;

    if (settingsId > 0) {
      this.jobCombinationService.getExcludedCustomers(settingsId).then((response: any[]) => {
        this.excludedCount = response.length;

        console.log('GOT EXCLUDED CUSTOMER LIST ');
        console.log(response);

        response.forEach((x) => {
          var item = <SelectItem>{ value: x.customer.id, label: x.customer.name };

          let index = included.find((i) => i.value === x.customer.id);

          if (index != null) {
            let index2 = included.indexOf(index);
            included = included.slice(0, index2).concat(included.slice(index2 + 1));
          }

          excluded = [...excluded, item];
        });

        this.excludedCustomers = excluded;
        this.includedCustomers = included;
      });
    }
  }

  async onTabChange(event: any) {
    console.log('Tab changed: ', event);

    if (event.index == 1) {
      console.log(
        'loaded id: ',
        this.loadedMatchesId,
        ' - selected settings: ',
        this.selectedSettingsId,
      );

      // load current profile matches if not already loaded
      if (this.loadedMatchesId != this.selectedSettingsId) {
        this.loadingMatches = true;
        this.profileMatches = await this.jobCombinationService.getAutoMatchedJobsByProfile(
          this.selectedSettingsId,
        );
        this.setMatchedJobsData();

        console.log('loaded matching jobs: ', this.profileMatches);

        this.loadedMatchesId = this.selectedSettingsId;
        this.loadingMatches = false;
      }
    }
  }

  // create summary data array from matched jobs for display in datagrid
  setMatchedJobsData() {
    this.matchedJobsData = this.profileMatches.map((pm) => {
      let firstOrder = pm.orders[0];
      let firstTask = firstOrder.tasks[0];

      let lastOrder = pm.orders[pm.orders.length - 1];
      let lastTask = lastOrder.tasks[lastOrder.tasks.length - 1];

      let totalDistance =
        pm.orders.reduce((a, b) => a + b.tasks.reduce((t1, t2) => t1 + t2.distance, 0), 0) *
        0.000621371;

      return {
        id: pm.id,
        jobNumber: pm.jobNumber,
        from: firstTask.pickupPostcode,
        to: lastTask.dropoffPostcode,
        totalDistance,
      };
    });
  }

  onGridLazyLoad($event: any) {}

  showMatchData($event: any) {
    console.log('Showing match data: ', this.selectedMatchedJob);
    // let match = this.profileMatches.find(x => x == $event.data);
    this.viewMatch = this.profileMatches.filter(
      (x) => x.jobNumber == this.selectedMatchedJob.jobNumber,
    )[0];
  }
}
